import { getUnixTime, fromUnixTime, intervalToDuration } from 'date-fns';
import type { EChartsType } from 'echarts/core';
import type { GridSortModel } from '@mui/x-data-grid';
import { calculateDurationBetweenDates } from '@/lib/helpers/durationHelpers';
import {
    DEX,
    FavoriteLimits,
    MODAL_TYPE_HANDLER,
    RANGE_TYPES,
} from '@/lib/types/hypertrackerTypes';
import { ClosedTrade } from '@/state/hypertracker/hypertrackerTypes';
import { SelectValue } from '@/components/Common/Select';
import { timeframes } from '@/lib/constants/generalConstants';

/** Prepends the bucket base URL to a relative path to form a full S3 asset URL. */
export const createUrl = (path: string) => {
    return `${import.meta.env.REACT_APP_BUCKET_URL}/aggregator${path}`;
};

/** Swaps perp-based values of a columnar chart row for their perp margin counterparts. */
export const withMarginColumns = <R extends unknown[]>(
    columns: readonly string[],
    row: R,
    replacements: Record<string, string>,
): R => {
    const next = [...row] as R;
    Object.entries(replacements).forEach(([column, marginColumn]) => {
        const index = columns.indexOf(column);
        const marginIndex = columns.indexOf(marginColumn);
        // keep the perp value when either column is missing from the JSON or has no value
        if (index !== -1 && marginIndex !== -1 && row[marginIndex] != null) {
            next[index] = row[marginIndex];
        }
    });
    return next;
};

/**
 * Timestamp of the row where a columnar chart switches from perp to perp margin values (BE-858).
 * Rows from before the release carry the margin column as null or as a copy of the perp one, so
 * the switch is the first row where they differ. Undefined when the switch is outside the series.
 */
export const findMarginSwitchTimestamp = (
    columns: readonly string[],
    rows: readonly unknown[][],
    replacements: Record<string, string>,
): string | undefined => {
    const columnPairs = Object.entries(replacements)
        .map(([column, marginColumn]) => [columns.indexOf(column), columns.indexOf(marginColumn)])
        .filter(([index, marginIndex]) => index !== -1 && marginIndex !== -1);
    const switchIndex = rows.findIndex((row) =>
        columnPairs.some(
            ([index, marginIndex]) => row[marginIndex] != null && row[marginIndex] !== row[index],
        ),
    );
    // a switch on the first row means the whole series is already on margin: no step to explain
    return switchIndex > 0 ? String(rows[switchIndex][0]) : undefined;
};

/**
 * Releases that changed how the backend counts wallets without changing the JSON columns, so the
 * switch can't be found from the data (FE-1356). Each value falls between the last old-method row
 * and the first new-method row of that environment.
 */
const METHODOLOGY_SWITCH_TS = {
    totalWalletsOnHL: { dev: '2026-09-18T12:00:00Z', prod: '2026-10-05T12:00:00Z' },
    hypeTokenHolders: { dev: '2026-09-14T12:00:00Z', prod: '2026-10-05T12:00:00Z' },
};

/**
 * Timestamp of the first [timestamp, value] point counted with the new method on a chart from
 * METHODOLOGY_SWITCH_TS. Undefined when the switch is outside the series or this line shows no
 * step there, e.g. a value filter the methodology change didn't affect.
 */
export const findMethodologySwitchTimestamp = (
    chart: keyof typeof METHODOLOGY_SWITCH_TS,
    points: readonly (string | number)[][],
): string | undefined => {
    const env = import.meta.env.REACT_APP_ENVR === 'dev' ? 'dev' : 'prod';
    const switchTime = new Date(METHODOLOGY_SWITCH_TS[chart][env]).getTime();
    const switchIndex = points.findIndex(([time]) => new Date(time).getTime() >= switchTime);
    // a switch on the first point means the whole series is already on the new method
    if (switchIndex <= 0) return undefined;

    const values = points.map(([, value]) => Number(value));
    // changes[i] is the relative step from values[i] to values[i + 1]
    const changes = values
        .slice(1)
        .map((value, index) => Math.abs(value - values[index]) / Math.max(values[index], 1));
    const typicalChange = [...changes].sort((a, b) => a - b)[Math.floor(changes.length / 2)];
    const switchChange = changes[switchIndex - 1];

    // unaffected lines stay below ~4.5x their median daily change, affected ones start at ~5.3x.
    // No check on the step's share of the line's range: HYPE Token Holders "All" steps ~10x its
    // usual change but only ~3% of its range, and still needs the note (FE-1356 QA).
    const isUnusual = switchChange > 0 && switchChange >= typicalChange * 5;

    return isUnusual ? String(points[switchIndex][0]) : undefined;
};

/** Default text of the methodology note on the cohort bias and exposure charts. */
export const METHODOLOGY_NOTE_BIAS_EXPOSURE =
    "Now measured against margin equity, which includes spot balances used as margin. Values move closer to 0 from this point. Earlier points can't be recalculated.";
/** Tooltip block explaining the step at the perp → perp margin switch point. */
export const methodologyNoteHtml = (title: string, text: string) => `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(160, 162, 164, 0.3);max-width:260px;white-space:normal;">
        <p style="margin:0 0 4px;font-size:12px;font-weight:600;">${title}</p>
        <p style="margin:0;font-size:11px;line-height:15px;">${text}</p>
    </div>
`;

/** How close (in px along the x axis) the hovered point must be to the switch to show the note. */
const METHODOLOGY_NOTE_HOVER_RADIUS_PX = 2;

/**
 * Whether the hovered point is within a few pixels of the perp → perp margin switch point. On long
 * ranges the points around the step share a pixel and the axis tooltip may snap to any of them.
 * x values are timestamps on a time axis and indices on a category axis (-1 from findIndex = no switch).
 */
export const isNearMarginSwitch = (
    chart: Pick<EChartsType, 'convertToPixel'> | undefined,
    hoveredX: number | undefined,
    switchX: number | undefined,
) => {
    if (!chart || hoveredX == null || switchX == null || switchX < 0) return false;
    const toPixel = (x: number) => Number(chart.convertToPixel({ xAxisIndex: 0 }, x));
    return Math.abs(toPixel(hoveredX) - toPixel(switchX)) <= METHODOLOGY_NOTE_HOVER_RADIUS_PX;
};

/** Chart point marking the perp → perp margin switch, so the step is hoverable. */
export const methodologyMarkPoint = (
    coord: (string | number)[],
    color: string,
    backgroundColor = 'transparent',
) => ({
    name: 'Methodology update',
    coord,
    symbol: 'circle',
    symbolSize: 8,
    itemStyle: { color: backgroundColor, borderColor: color, borderWidth: 2 },
    label: { show: false },
});

/** Returns true if the string is a valid Hyperliquid wallet address (0x-prefixed, 40 hex chars). */
export const isValidHyperliquidAddress = (wallet: string): boolean =>
    /^0x[a-fA-F0-9]{40}$/.test(wallet);

/** Maps an exposure ratio value (0–40+) to a hex color ranging from green (safe) to red (high risk). */
export const defineExposureColor = (ratio: number) => {
    if (ratio > 0 && ratio <= 3) {
        return '#38CB89';
    }

    if (ratio > 3 && ratio <= 7) {
        return '#67C577';
    }

    if (ratio > 7 && ratio <= 13) {
        return '#96BF65';
    }

    if (ratio > 13 && ratio <= 20) {
        return '#C5B953';
    }

    if (ratio > 20 && ratio <= 30) {
        return '#E39A51';
    }

    if (ratio > 30 && ratio < 40) {
        return '#F47C4F';
    }

    if (ratio >= 40) {
        return '#FD6E4E';
    }

    return '#A0A2A4';
};

/** Maps a bias value (−1 to 1) to a bull_meter.* i18n translation key for the market bias label. */
export const defineBiasLabel = (bias: number) => {
    if (bias === 0) {
        return 'bull_meter.indecisive_label';
    }

    if (bias >= -0.04 && bias < 0.04) {
        return 'bull_meter.indecisive_label';
    }

    if (bias >= 0.75) {
        return 'bull_meter.very_bullish_label';
    }

    if (bias >= 0.35) {
        return 'bull_meter.bullish_label';
    }
    if (bias >= 0.04) {
        return 'bull_meter.slightly_bullish_label';
    }

    if (bias >= -0.35) {
        return 'bull_meter.slightly_bearish_label';
    }

    if (bias >= -0.75) {
        return 'bull_meter.bearish_label';
    }
    if (bias < -0.75) {
        return 'bull_meter.very_bearish_label';
    }
    return 'bull_meter.indecisive_label';
};

/** Returns a formatted elapsed-time string and an `isNew` flag (true if < 7 days old) for a timestamp. */
export const getAge = (value: number) => {
    const startEnd = {
        start: new Date(value),
        end: new Date(),
    };
    let units: any[] = [];
    const interval: any = intervalToDuration(startEnd);

    if (interval?.years > 0) {
        units = ['days'];
    } else if (interval?.months > 0) {
        units = ['days'];
    } else if (interval?.days > 0) {
        units = ['days'];
    } else if (interval?.hours > 0) {
        units = ['hours'];
    } else if (interval?.minutes > 0) {
        units = ['minutes'];
    } else if (interval?.seconds > 0) {
        units = ['seconds'];
    }
    const str = calculateDurationBetweenDates(new Date(value), new Date(), {
        units,
        showJustBiggestUnit: true,
    });

    const isNew = !interval.years && !interval.months && (!interval.days || interval.days < 7);

    return { str, isNew };
};

const TIMESTAMP_AGE_SORT_FIELDS = ['age', 'openTime', 'earliestActivityAt'];

export const invertAgeSortModel = (sortModel: GridSortModel): GridSortModel =>
    sortModel.map((item) =>
        TIMESTAMP_AGE_SORT_FIELDS.includes(item.field) && item.sort
            ? { ...item, sort: item.sort === 'asc' ? 'desc' : 'asc' }
            : item,
    );

/** Ordered palette of 20 distinct hex colors for multi-line Hypertracker charts. */
export const darkLineColors: string[] = [
    '#14c8d4', // cyan
    '#007bff', // blue
    '#f5a623', // amber
    '#ff6b5c', // coral
    '#cccccc', // light gray
    '#f54291', // pink
    '#9b59b6', // purple
    '#00b894', // teal
    '#f39c12', // orange
    '#3498db', // soft blue
    '#1abc9c', // greenish cyan
    '#e74c3c', // red
    '#8e44ad', // deep purple
    '#e67e22', // dark orange
    '#2ecc71', // green
    '#e84393', // hot pink
    '#d35400', // pumpkin
    '#7f8c8d', // mid gray
    '#ff9ff3', // lavender pink
    '#00cec9', // aquamarine
];

/** Returns the S3 JSON filename for builders analytics data for the given time range (day/week/all/30d). */
export const getBuildersUrl = (range) => {
    switch (range) {
        case RANGE_TYPES.DAY:
            return '/builders_24h.json';
        case RANGE_TYPES.WEEK:
            return '/builders_7d.json';
        case RANGE_TYPES.ALL:
            return '/builders_all.json';
        default:
            return '/builders_30d.json';
    }
};

/** Adjusts an ECharts tooltip's [x, y] position to prevent it from overflowing viewport edges; supports x/y/both axis restriction. */
export const chartTooltipPosition = ({
    pos,
    size,
    restriction,
}: {
    pos: any;
    size: any;
    restriction?: 'x' | 'y';
}) => {
    const [mouseX, mouseY] = pos;
    const { viewSize, contentSize } = size;

    let tooltipX = mouseX;
    let tooltipY = mouseY;

    if (restriction === 'x') {
        if (mouseX + contentSize[0] > viewSize[0]) {
            tooltipX = viewSize[0] - contentSize[0] - 10;
        }
    } else if (restriction === 'y') {
        if (mouseY + contentSize[1] > viewSize[1]) {
            tooltipY = viewSize[1] - contentSize[1] - 20;
        }
    } else {
        if (mouseX + contentSize[0] > viewSize[0]) {
            tooltipX = viewSize[0] - contentSize[0] - 10;
        }
        if (mouseY + contentSize[1] > viewSize[1]) {
            tooltipY = viewSize[1] - contentSize[1] - 20;
        }
    }

    return [tooltipX, tooltipY];
};

/** Returns true if two file links refer to the same resource, ignoring the "aggregator" path prefix. */
export const compareFileLinks = (updatedLink: string, fileLink: string): boolean => {
    return updatedLink.replace('aggregator', '') === fileLink;
};

/** Determines which favorites restriction modal to show: login prompt only — favorites count limits are disabled. */
export const getFavoriteRestrictionModalVariant = (
    isMemberAuthorized: boolean,
    _isFavorite: boolean,
    _favoriteLimits: FavoriteLimits | undefined,
) => {
    if (!isMemberAuthorized) {
        return MODAL_TYPE_HANDLER.IS_LOGIN_TO_ADD_FAVORITES;
    }

    return null;
};

/** Returns true if the token string is in DEX format ("exchange:COIN"), e.g. "dex:ETH". */
export const isDex = (token: string): boolean => {
    if (!token) return false;

    const parts = token.split(':');
    let isDexToken = false;
    if (parts.length === 2 && Object.values(DEX).includes(parts[0].toLowerCase() as DEX)) {
        isDexToken = true;
    }

    return isDexToken;
};

/** Extracts the coin ticker from a DEX token string ("dex:COIN" → "COIN"); returns the token as-is for non-DEX tokens. */
export const getCoinName = (coin: string | null | undefined, withCoinWord?: boolean) => {
    const defaultWord = withCoinWord ? 'COIN' : '';
    if (!coin) return defaultWord;
    const isDexToken = isDex(coin || '');
    return isDexToken ? coin.split(':')?.[1] : coin;
};

/** Inverts a DEX token pair string ("dex:COIN" → "COIN:dex"); returns non-DEX tokens unchanged. */
export const getDexCoinInverseName = (coin: string | null | undefined): string => {
    if (!coin) return '';
    const isDexToken = isDex(coin || '');
    if (!isDexToken) return coin;

    const parts = coin.split(':');
    return `${parts[1]}:${parts[0]}`;
};

/** Maps a closed trade's duration to the nearest timeframe bucket (e.g. "1m", "5m", "1h"); defaults to the second timeframe if no match. */
export const definedTimeframeHT = (row: ClosedTrade): SelectValue => {
    if (row) {
        const tradeDuplicate = { ...row } as ClosedTrade;
        const duration =
            getUnixTime(new Date(tradeDuplicate.closeTime)) -
            getUnixTime(new Date(tradeDuplicate.openTime));

        const tfTemp = timeframes.find(
            (tfItem) => duration >= tfItem.duration.from && duration < tfItem.duration.to,
        );

        let result: any = null;

        if (tfTemp) {
            result = { name: tfTemp.timeframe, selectValue: `${tfTemp.seconds}` };
        } else if (duration < timeframes[0].duration.from) {
            result = { name: timeframes[0].timeframe, selectValue: `${timeframes[0].seconds}` };
        } else if (duration >= timeframes[timeframes.length - 1].duration.to) {
            result = {
                name: timeframes[timeframes.length - 1].timeframe,
                selectValue: `${timeframes[timeframes.length - 1].seconds}`,
            };
        }

        if (result) return result;
    }
    return { name: timeframes[1].timeframe, selectValue: `${timeframes[1].seconds}` };
};

/** Calculates candle chart start/end dates and candle count limit to frame a trade with symmetric padding on each side. */
export const getCandleHistoryParameters = (tradeDuplicate: ClosedTrade, tfSeconds: number) => {
    const MIN_CANDLES = 40;

    const duration =
        getUnixTime(new Date(tradeDuplicate.closeTime)) -
        getUnixTime(new Date(tradeDuplicate.openTime));

    const durationCandles = Math.floor(duration / tfSeconds);
    let maxCandles = 70;

    if (duration > timeframes[timeframes.length - 1].duration.to) {
        maxCandles = durationCandles + 250;
    }

    if (durationCandles >= maxCandles) {
        maxCandles = durationCandles + 100;
    }

    if (tfSeconds === 1) {
        maxCandles = Math.min(maxCandles, 600);
    }

    const idealSideCandles = Math.floor((maxCandles - durationCandles) / 2);
    const nowUnix = getUnixTime(new Date());
    const closeUnix = getUnixTime(new Date(tradeDuplicate.closeTime));
    const openUnix = getUnixTime(new Date(tradeDuplicate.openTime));

    const availableRightCandles = Math.floor(Math.max(0, nowUnix - closeUnix) / tfSeconds);
    const rightPaddingCandles = Math.min(idealSideCandles, availableRightCandles);

    const leftPaddingCandles = Math.max(
        idealSideCandles,
        MIN_CANDLES - durationCandles - rightPaddingCandles,
    );

    const start = openUnix - leftPaddingCandles * tfSeconds;
    const end = closeUnix + rightPaddingCandles * tfSeconds;

    const totalCandles = leftPaddingCandles + durationCandles + rightPaddingCandles;
    const limit = Math.min(totalCandles, 1000);

    return {
        start: fromUnixTime(start).toISOString(),
        end: fromUnixTime(end).toISOString(),
        limit,
    };
};
